<template>
	<section
		style="display: flex; flex-direction: column; margin-bottom: 20px; gap: 20px"
	>
		<div class="recherche">
			<!-- Champ de recherche -->
			<VCombobox
				v-model="selectedCity"
				data-test="search-input"
				:search-input.sync="search"
				:items="citiesDisplayNames"
				:filter="(item) => item"
				autofocus
				:loading="loadingInput"
				hint="Recherche par nom ou code postal"
				label="Rechercher une ville"
				style="max-width: 400px; min-width: 300px"
			/>

			<!-- Filtres -->
			<div
				class="d-flex flex-column flex-sm-row"
				style="gap: 20px"
			>
				<!-- Filtre accès handicapés -->
				<VSwitch v-model="disabledPeopleFilter">
					<template #label>
						Accès&nbsp;handicapés
					</template>
				</VSwitch>

				<!-- Filtre mammographe adapté aux fauteuils -->
				<VSwitch v-model="chairMountedMammographFilter">
					<template #label>
						Avec&nbsp;mammographe&nbsp;adapté&nbsp;aux&nbsp;fauteuils
					</template>
				</VSwitch>
			</div>
		</div>

		<!-- Bouton de recherche -->
		<VBtn
			:loading="loadingSearch"
			color="primary"
			style="width: fit-content; align-self: center;"
			@click="searchStructures"
		>
			Rechercher
		</VBtn>
	</section>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from 'vue';
	import { debounce } from '@cnamts/vue-dot/src/functions/debounce';

	import { CityService } from '@/services/city-service';
	import { formatCityDisplayedName } from '../utils/postal-codes-utils';
	import { StructureApiService } from '@/services/structure-api-service';
	import { GeoApiCity } from '@/types/geo-api-city';
	import { Structure } from '@/types/structure';
	import { selectedStructureId } from '../store/store';
	import { TraceEventService } from '@/services/trace-event-service';

	const disabledPeopleFilter = ref(false);
	const chairMountedMammographFilter = ref(false);

	const search = ref('');
	const selectedCity = ref('');
	const loadingInput = ref(false);
	const cities = ref<GeoApiCity[]>([]);
	const citiesDisplayNames = computed(() => cities.value.map(c => formatCityDisplayedName(c.nom, c.codesPostaux, c.departement.nom)));

	const loadingSearch = ref(false);

	const SEARCH_DELAY_MILLISECONDS = 500;
	const SEARCH_MIN_CHARACTERS_TRIGGER = 2;

	const emits = defineEmits<{
		(e: 'on-change-structures-list', structureList: Structure[]): void;
		(e: 'on-change-city', city: GeoApiCity): void;
	}>();

	/**
	 * Trigger la recherche des villes dès que l'utilisateur saisi plus de 2 caractères
	 */
	watch(
		search,
		debounce(async() => {
			if (!citiesDisplayNames.value.includes(search.value) && search.value?.length > SEARCH_MIN_CHARACTERS_TRIGGER) {
				try {
					loadingInput.value = true;

					cities.value = await CityService.getCityList(search.value);
				} finally {
					loadingInput.value = false;
				}
			}
		}, SEARCH_DELAY_MILLISECONDS)
	);

	/**
	 * Lance la recherche des structures en appelant l'API
	 */
	function searchStructures(): void {
		const city = cities.value.find(c => formatCityDisplayedName(c.nom, c.codesPostaux, c.departement.nom) === selectedCity.value);
		if (city) {
			emits('on-change-city', city);
			loadingSearch.value = true;

			// On trace la recherche des structures
			TraceEventService.trace('RECHERCHE');

			StructureApiService.getStructuresListByCoordinatesAndFeatures(
				city.centre.coordinates[0],
				city.centre.coordinates[1],
				disabledPeopleFilter.value,
				chairMountedMammographFilter.value
			)
				.then((structuresList) => {
					emits('on-change-structures-list', structuresList);
					selectedStructureId.value = null;
				})
				.finally(() => {
					loadingSearch.value = false;
				});
		}
	}

</script>

<style scoped>
.recherche {
	display: flex;
	gap: 2rem;
	justify-content: center;
}

/* Style appliqué sur les écrans dont la largeur est supérieure à 700px */
@media screen and (max-width: 700px) {
	.recherche {
		flex-direction: column;
		align-items: center;
	}
}
</style>
